import { convertArrayToCSV } from 'convert-array-to-csv'
import {
  addDays,
  addMonths,
  addWeeks,
  format,
  isDate,
  isSameDay,
  parseISO,
} from 'date-fns/fp'
import isArray from 'lodash/isArray'

function startOfMonth(date) {
  const firstOfMonth = !date ? new Date() : new Date(date.valueOf())
  firstOfMonth.setDate(1)
  return firstOfMonth
}

function endOfMonth(date) {
  let lastOfMonth = !date ? new Date() : new Date(date.valueOf())
  lastOfMonth = addMonths(1, lastOfMonth)
  lastOfMonth.setDate(0)
  return lastOfMonth
}

function buildMonth(start, month, hasEventsCallback) {
  start.setDate(1)
  // the first previous Sunday to fill with dates from previous month
  start.setDate(-start.getDay() + 1)
  const weeks = []
  let done = false
  let date = start
  let monthIndex = date.getMonth()
  let count = 0

  while (!done) {
    weeks.push({
      days: _buildWeek(date, month, hasEventsCallback),
    })
    date = addWeeks(1, date)
    done = count++ > 2 && monthIndex !== date.getMonth()
    monthIndex = date.getMonth()
  }

  return weeks

  function _buildWeek(date, month, hasEventsCallback) {
    const days = []
    const today = new Date()
    for (let i = 0; i < 7; i++) {
      days.push({
        name: format('d', date),
        number: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isToday: isSameDay(date, today),
        date: date,
        hasEvents: hasEventsCallback(date),
      })
      date = addDays(1, date)
    }
    return days
  }
}

// // may be used to filter events locally by date
// // not used at the moment
// function filterDateInterval(events, datePropertyName, startDate, endDate) {
//   const startDay = new Date(startDate.valueOf()).setHours(0, 0, 0, 0)
//   const endDay = new Date(endDate.valueOf()).setHours(0, 0, 0, 0)

//   return events.filter((event) =>
//     isDateInInterval(parseISO(event[datePropertyName]), {
//       start: startDay,
//       end: endDay,
//     })
//   )

//   function isDateInInterval(date, { start, end }) {
//     return isAfter(date, start) && isBefore(date, end)
//   }
// }

// internal
function prepareParamsForRequest(params) {
  const formattedParams = Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      let formattedValue = value
      if (isDate(value)) formattedValue = format('yyyy-MM-dd', value)
      return [key, isArray(value) ? value : encodeURIComponent(formattedValue)]
    })
  )

  return formattedParams
}

function prepareTagsForVueTagsInput(tags) {
  return tags.map(({ id, name }) => ({ id, text: name }))
}

function setFiltersToCurrentUrl(filters) {
  const { location, history } = window

  // Clear history before setting the new query
  history.replaceState({}, '', location.pathname)

  const params = new URLSearchParams(window.location.search)
  Object.entries(prepareParamsForRequest(filters)).map(([key, value]) =>
    params.set(key, encodeURIComponent(value))
  )

  window.localStorage.setItem(
    'events_with_filters',
    decodeURIComponent(`${location.pathname}?${params}`)
  )

  return history.replaceState(
    {},
    '',
    decodeURIComponent(`${location.pathname}?${params}`)
  )
}

function getFiltersFromCurrentUrl(filters, { query }) {
  const unformattedQuery = Object.fromEntries(
    Object.entries(query).map(([key, value]) => [
      key,
      decodeURIComponent(value),
    ])
  )

  const {
    from_date: fromDate,
    to_date: toDate,
    cause_ids: causeIds,
    tag_ids: tagIds,
  } = unformattedQuery

  const parseIds = (ids) =>
    (ids && ids.split(',').map((value) => parseInt(value))) || []

  return {
    ...filters,
    ...unformattedQuery,
    from_date: fromDate ? parseISO(fromDate) : startOfMonth(new Date()),
    to_date: toDate ? parseISO(toDate) : endOfMonth(new Date()),
    cause_ids: parseIds(causeIds),
    tag_ids: parseIds(tagIds),
  }
}

function convertToCSV(objArray) {
  const events = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray

  const header = [
    'Event Name',
    'Event Date',
    'Start Time',
    'End Time',
    'Spots Available',
    'Spots Booked',
    'Checked In',
    'City',
    'Zip Code',
    'Event Leader',
    'Day of Week',
    'Month',
    '# on waitlist',
    'Cause tags',
    'Other tags',
    'Group',
  ]

  const rows = events.map((event) => {
    const leaders =
      (event.leaders && event.leaders.map(({ name }) => name)) || []
    const causes = (event.causes && event.causes.map(({ name }) => name)) || []
    const tags = (event.tags && event.tags.map(({ name }) => name)) || []

    return [
      event.name,
      format('MM-dd-yyyy', parseISO(event.start)),
      format('hh:mm', parseISO(event.start)),
      format('hh:mm', parseISO(event.end)),
      event.max_people,
      event.users_count,
      event.checked_in_count,
      event.city,
      event.zip,
      leaders.join(';'),
      format('dddd', parseISO(event.start)),
      format('MMMM', parseISO(event.start)),
      (event.waitlist_users && Object.keys(event.waitlist_users).length) || '',
      causes.join(';'),
      tags.join(';'),
      event.group_name,
    ]
  })

  return convertArrayToCSV(rows, { header })
}

export {
  buildMonth,
  endOfMonth,
  getFiltersFromCurrentUrl,
  prepareParamsForRequest,
  prepareTagsForVueTagsInput,
  setFiltersToCurrentUrl,
  startOfMonth,
  convertToCSV,
}
